import { useState, type FormEvent } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Drawer, Button, cn } from '../../design'
import { createSprint, type Sprint, type SprintStatus } from '../../api/sprints'

const SPRINT_STATUSES: SprintStatus[] = ['planned', 'active', 'closed']

const FORM_ID = 'new-sprint-form'

const inputCls =
  'w-full rounded-md border border-border bg-bg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent'

/**
 * Create-sprint drawer.
 *
 * Empty optional fields are omitted rather than sent blank: the backend's
 * validate() skips empty strings but still passes them through to the INSERT,
 * so a blank start_date/end_date would land in a DATE column.
 *
 * The backend does not check that end_date > start_date, nor that only one
 * sprint is active at a time — both are enforced here.
 */
export function NewSprintDrawer({
  projectId,
  open,
  onOpenChange,
  hasActiveSprint = false,
  onCreated,
}: {
  projectId: number
  open: boolean
  onOpenChange: (open: boolean) => void
  /** Warn before creating a second active sprint. */
  hasActiveSprint?: boolean
  onCreated?: (sprint: Sprint) => void
}) {
  const qc = useQueryClient()

  const [name, setName] = useState('')
  const [goal, setGoal] = useState('')
  const [startDate, setStartDate] = useState('')
  const [endDate, setEndDate] = useState('')
  const [capacity, setCapacity] = useState('')
  const [status, setStatus] = useState<SprintStatus>('planned')

  const createMut = useMutation({
    mutationFn: () =>
      createSprint({
        project_id: projectId,
        name: name.trim(),
        status,
        ...(goal.trim() ? { goal: goal.trim() } : {}),
        ...(startDate ? { start_date: startDate } : {}),
        ...(endDate ? { end_date: endDate } : {}),
        ...(capacity.trim() !== '' ? { capacity_points: Number(capacity) } : {}),
      }),
    onSuccess: (sprint) => {
      qc.invalidateQueries({ queryKey: ['sprints', projectId] })
      qc.invalidateQueries({ queryKey: ['lookups', 'sprints', projectId] })
      onCreated?.(sprint)
      onOpenChange(false)
    },
  })


  const datesInvalid = !!startDate && !!endDate && endDate < startDate
  const capacityInvalid =
    capacity.trim() !== '' && (Number.isNaN(Number(capacity)) || Number(capacity) < 0)
  const canSubmit =
    name.trim().length > 0 && !datesInvalid && !capacityInvalid && !createMut.isPending

  function onSubmit(e: FormEvent) {
    e.preventDefault()
    if (!canSubmit) return
    createMut.mutate()
  }

  return (
    <Drawer
      open={open}
      onOpenChange={onOpenChange}
      width="md"
      title="New sprint"
      description="Only a name is required."
      footer={
        <div className="flex items-center justify-end gap-2">
          {createMut.isError && (
            <span className="mr-auto text-xs text-danger">
              Could not create sprint. Please try again.
            </span>
          )}
          <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button type="submit" form={FORM_ID} disabled={!canSubmit}>
            {createMut.isPending ? 'Creating…' : 'Create sprint'}
          </Button>
        </div>
      }
    >
      <form id={FORM_ID} onSubmit={onSubmit} className="space-y-4">
        <label className="block">
          <span className="block text-xs font-medium text-fg-muted mb-1.5">
            Name<span className="text-danger"> *</span>
          </span>
          <input
            value={name}
            onChange={e => setName(e.target.value)}
            required
            autoFocus
            placeholder="e.g. Sprint 1"
            className={inputCls}
          />
        </label>

        <label className="block">
          <span className="block text-xs font-medium text-fg-muted mb-1.5">Goal</span>
          <textarea
            value={goal}
            onChange={e => setGoal(e.target.value)}
            rows={3}
            placeholder="What should this sprint achieve?"
            className={cn(inputCls, 'resize-y')}
          />
        </label>

        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className="block text-xs font-medium text-fg-muted mb-1.5">Start date</span>
            <input
              type="date"
              value={startDate}
              onChange={e => setStartDate(e.target.value)}
              className={inputCls}
            />
          </label>
          <label className="block">
            <span className="block text-xs font-medium text-fg-muted mb-1.5">End date</span>
            <input
              type="date"
              value={endDate}
              onChange={e => setEndDate(e.target.value)}
              className={inputCls}
            />
          </label>
        </div>
        {datesInvalid && (
          <p className="text-xs text-danger">End date must be on or after the start date.</p>
        )}

        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className="block text-xs font-medium text-fg-muted mb-1.5">Capacity (points)</span>
            <input
              type="number"
              min={0}
              step={1}
              value={capacity}
              onChange={e => setCapacity(e.target.value)}
              placeholder="—"
              className={inputCls}
            />
          </label>
          <label className="block">
            <span className="block text-xs font-medium text-fg-muted mb-1.5">Status</span>
            <select
              value={status}
              onChange={e => setStatus(e.target.value as SprintStatus)}
              className={inputCls}
            >
              {SPRINT_STATUSES.map(s => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>
          </label>
        </div>
        {capacityInvalid && (
          <p className="text-xs text-danger">Capacity must be zero or greater.</p>
        )}
        {status === 'active' && hasActiveSprint && (
          <p className="text-xs text-warning">
            This project already has an active sprint. Having two at once makes the planning
            view ambiguous — consider creating this one as “planned”.
          </p>
        )}
      </form>
    </Drawer>
  )
}
